import Route from '@ember/routing/route'
import { service } from '@ember/service'
import { tracked } from '@glimmer/tracking'
import RSVP from 'rsvp'
import type Store from '@ember-data/store'
import type Transition from '@ember/routing/transition'
import type ReportingUiScopeRoute from 'teacher-ui-client/reporting/ui-scope/route'
import type { StudentScope } from 'teacher-ui-client/reporting/ui-scope/utils'
import type { NormalizedInterval } from 'date-fns'
import { assert } from '@ember/debug'
import type { MentalMinuteSummaryData } from 'teacher-ui-client/components/stat-boxes/fluency-sprints'
import type StudentEventsService from 'teacher-ui-client/services/student-events'
import type Student from 'teacher-ui-client/models/student'
import { hasId } from 'teacher-ui-client/utils/type-guards'

interface Params extends Record<string, unknown> {
  student_id: string
}

export interface MentalMinuteStatsResponse {
  mentalMinuteSummary: Nullable<MentalMinuteSummaryData>
}

export class MathsFluencySprintsStudentRoute extends Route {
  @service studentEvents!: StudentEventsService

  @service store!: Store

  @tracked previousRouteName = 'reporting.ui-scope.maths.fluency-sprints.teacher' // default

  async model(params: Params, transition: Transition) {
    // Remember the previous route that wasn't part of this student area, for the back link to return to
    const currentRouteName = this.fullRouteName
    const transitionFromRouteName = transition.from?.name

    // Check that the transition isn't just from another student selection in the dropdown, or some other child route
    const isTransitionFromNameValid = transitionFromRouteName && !transitionFromRouteName.includes(currentRouteName)

    const previousRouteName = isTransitionFromNameValid ? transitionFromRouteName : this.previousRouteName
    this.previousRouteName = previousRouteName

    const { scopedData, period } = this.modelFor('reporting.ui-scope') as ModelFor<ReportingUiScopeRoute>

    const { student_id: studentId } = params

    const statsScope: StudentScope = { scope: 'student', ids: [studentId] }

    const product: LegacyProductMs = 'maths'
    const stats = await this.makeStatsRequests(product, statsScope, period)

    const student = this.store.peekRecord<Student>('student', studentId)
    assert('could not find student in ember-data store', student && hasId(student))

    return {
      product,
      students: scopedData.students,
      student,
      previousRouteName,
      period,
      ...stats,
    }
  }

  makeStatsRequests = (
    product: LegacyProductMs,
    scope: StudentScope,
    period: NormalizedInterval,
  ): Promise<MentalMinuteStatsResponse> => {
    return RSVP.hash({
      mentalMinuteSummary: this.studentEvents.mentalMinuteSummary(product, scope, period),
    })
  }
}

export default MathsFluencySprintsStudentRoute
